import { useCallback, useEffect, useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { atomFamily, selectAtom } from 'jotai/utils'

import { COMPLETED_ACTION_DURATION_MS } from '@renderer/overlay/Sidepanel/hooks/staged-action-status'
import { pendingDisplayAtom } from '@renderer/overlay/Sidepanel/hooks/staged-action-local-state'
import { useVisibleStagedActions } from '@renderer/overlay/Sidepanel/hooks/staged-actions/visible-actions-context'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { forYouLingerAtom, type ForYouLingerEntry } from './for-you-linger-state'
import { selectOutstandingActions, type SidepanelChat } from './running-chat-selection'

const forYouLingerEntryAtom = atomFamily((id: string) => selectAtom(forYouLingerAtom, (entries) => entries.get(id)))

forYouLingerEntryAtom.setShouldRemove((createdAt) => Date.now() - createdAt > 60 * 60 * 1000)

/** Drops rows whose linger has already faded out, so they cannot snap back at full opacity. */
export function hideElapsedForYouLinger(
  chats: Array<SidepanelChat>,
  lingering: ReadonlyMap<string, ForYouLingerEntry>,
): Array<SidepanelChat> {
  if (lingering.size === 0) return chats
  return chats.filter((chat) => !lingering.get(chat.id)?.elapsed)
}

export function getForYouDisplayChats(
  chats: Array<SidepanelChat>,
  lingering: ReadonlyMap<string, ForYouLingerEntry>,
  feedKey: string,
): Array<SidepanelChat> {
  if (lingering.size === 0) return chats

  const activeIds = new Set(chats.map((chat) => chat.id))
  const display = hideElapsedForYouLinger(chats, lingering).slice()
  const missing = [...lingering.values()]
    .filter((entry) => !entry.elapsed && entry.feedKey === feedKey && !activeIds.has(entry.chat.id))
    .sort((a, b) => a.position - b.position || a.startedAt - b.startedAt)

  for (const entry of missing) {
    display.splice(Math.min(entry.position, display.length), 0, entry.chat)
  }

  return display
}

export function markElapsedForYouLinger(
  lingering: Map<string, ForYouLingerEntry>,
  now: number,
): Map<string, ForYouLingerEntry> {
  let next: Map<string, ForYouLingerEntry> | null = null
  for (const [id, entry] of lingering) {
    if (entry.elapsed || entry.sharesTodosCountdown || now - entry.startedAt < COMPLETED_ACTION_DURATION_MS) continue
    next ??= new Map(lingering)
    next.set(id, { ...entry, elapsed: true })
  }
  return next ?? lingering
}

export function getReactivatedForYouLingerIds(
  chats: Array<SidepanelChat>,
  lingering: ReadonlyMap<string, ForYouLingerEntry>,
  actionableIds: ReadonlySet<string>,
): Array<string> {
  return chats.flatMap((chat) => {
    const entry = lingering.get(chat.id)
    if (!entry) return []
    // An executed action can come back — undone from the output view — and the row with
    // it. An archived row keeps its outstanding actions, so this only reads completions.
    if (entry.kind === 'executed' && actionableIds.has(chat.id)) return [chat.id]
    // A row archived mid-stream is still streaming, so only a stream that started after
    // the linger counts as new activity — otherwise the undo window never opens.
    const started = chat.isStreaming && !entry.chat.isStreaming
    return started || chat.previewRevision > entry.chat.previewRevision ? [chat.id] : []
  })
}

export function useForYouLingerEntry(id: string): ForYouLingerEntry | undefined {
  return useAtomValue(forYouLingerEntryAtom(id))
}

/** Every lingering row, for a row that stands for several chats at once. */
export function useForYouLingerEntries(): ReadonlyMap<string, ForYouLingerEntry> {
  return useAtomValue(forYouLingerAtom)
}

export function useForYouExecutedLinger(
  chats: Array<SidepanelChat>,
  feedKey: string,
  actions: ReadonlyMap<string, StagedAction>,
): void {
  const pendingDisplay = useAtomValue(pendingDisplayAtom)
  const { start: startLinger } = useForYouLingerControls()

  useEffect(() => {
    for (const [position, chat] of chats.entries()) {
      const outstanding = selectOutstandingActions(chat.stagedActionIds, actions)
      if (outstanding.length > 0) {
        continue
      }

      const executedActionId = chat.stagedActionIds.find((actionId) => {
        const action = actions.get(actionId)
        return action?.outputData != null && pendingDisplay.has(actionId)
      })
      if (!executedActionId) {
        continue
      }

      startLinger({
        chat,
        kind: 'executed',
        feedKey,
        actionId: executedActionId,
        position,
        startedAt: pendingDisplay.get(executedActionId) ?? Date.now(),
      })
    }
  }, [actions, chats, feedKey, pendingDisplay, startLinger])
}

export function useForYouLingerControls() {
  const setLingering = useSetAtom(forYouLingerAtom)

  const start = useCallback(
    (entry: ForYouLingerEntry) =>
      setLingering((previous) => {
        // A spent entry is replaced, so a row that comes back can linger again.
        const current = previous.get(entry.chat.id)
        if (current && !current.elapsed) return previous
        const next = new Map(previous)
        next.set(entry.chat.id, entry)
        return next
      }),
    [setLingering],
  )

  const cancel = useCallback(
    (conversationId: string) =>
      setLingering((previous) => {
        if (!previous.has(conversationId)) return previous
        const next = new Map(previous)
        next.delete(conversationId)
        return next
      }),
    [setLingering],
  )

  return { start, cancel }
}

/**
 * Resolves a For You feed against linger state and drives its expiry. Call it once per
 * feed, at the component that owns the feed and with that feed's key — the resolved list
 * is what a row count and a rendered list must agree on.
 *
 * It reads the feed before linger is applied, so it still sees a row a spent linger
 * hides — which is how an action undone elsewhere brings its row back.
 */
export function useForYouDisplayChats(chats: Array<SidepanelChat>, feedKey: string): Array<SidepanelChat> {
  const lingering = useAtomValue(forYouLingerAtom)
  const setLingering = useSetAtom(forYouLingerAtom)
  const { allActions } = useVisibleStagedActions()

  const actionableIds = useMemo(() => {
    const actions = new Map(allActions.map((action) => [action.id, action]))
    return new Set(
      chats.filter((chat) => selectOutstandingActions(chat.stagedActionIds, actions).length > 0).map((chat) => chat.id),
    )
  }, [allActions, chats])

  useEffect(() => {
    if (lingering.size === 0) return

    const reactivatedIds = getReactivatedForYouLingerIds(chats, lingering, actionableIds)
    if (reactivatedIds.length > 0) {
      setLingering((previous) => {
        const next = new Map(previous)
        reactivatedIds.forEach((id) => next.delete(id))
        return next
      })
      return
    }

    const now = Date.now()
    let nextDelay: number | null = null
    let hasElapsed = false

    for (const entry of lingering.values()) {
      if (entry.elapsed || entry.sharesTodosCountdown) continue
      const remaining = COMPLETED_ACTION_DURATION_MS - (now - entry.startedAt)
      if (remaining <= 0) {
        hasElapsed = true
      } else {
        nextDelay = nextDelay == null ? remaining : Math.min(nextDelay, remaining)
      }
    }

    if (hasElapsed) {
      setLingering((previous) => markElapsedForYouLinger(previous, Date.now()))
      return
    }

    if (nextDelay == null) return

    const timeout = setTimeout(
      () => setLingering((previous) => markElapsedForYouLinger(previous, Date.now())),
      nextDelay,
    )
    return () => clearTimeout(timeout)
  }, [actionableIds, chats, lingering, setLingering])

  return useMemo(() => getForYouDisplayChats(chats, lingering, feedKey), [chats, feedKey, lingering])
}
